import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { authService } from '../services/api';
import '../styles/AuthPages.css';

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

interface TokenVerification {
  valid: boolean;
  user?: {
    email: string;
    first_name: string;
    last_name: string;
  };
}

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');


  const [formData, setFormData] = useState<ResetPasswordFormData>({
    password: '',
    confirmPassword: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isVerifyingToken, setIsVerifyingToken] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string>('');
  const [tokenVerification, setTokenVerification] = useState<TokenVerification | null>(null);
  const [passwordRequirements, setPasswordRequirements] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState(false);




  // Verify token on component mount
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const data = await authService.verifyResetToken(token!);
        setTokenVerification(data);
      } catch (err: any) {
        console.error('Token verification error:', err);

        const apiError = err.response?.data;
        if (apiError?.code === 'INVALID_TOKEN') {
          setError('This reset link is invalid or has expired. Please request a new one.');
        } else {
          setError(apiError?.error || 'Unable to verify reset token. Please check your internet connection.');
        }
      } finally {
        setIsVerifyingToken(false);
      }
    };

    if (!token) {
      setError('Invalid or missing reset token');
      setIsVerifyingToken(false);
      return;
    }

    verifyToken();
  }, [token]);

  const { isAuthenticated } = useAuth();

  // Redirect if already authenticated
  if (isAuthenticated) {
    navigate('/');
    return null;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (error) setError('');
  };

  const validatePasswords = (): boolean => {
    if (!formData.password) {
      setError('Password is required');
      return false;
    }

    if (!formData.confirmPassword) {
      setError('Please confirm your password');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLoading || !token) return;

    if (!validatePasswords()) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await authService.resetPassword(token, formData.password, formData.confirmPassword);
      setIsSubmitted(true);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', {
          replace: true,
          state: { message: 'Password reset successfully! Please log in with your new password.' }
        });
      }, 3000);
    } catch (err: any) {
      console.error('Password reset error:', err);

      const apiError = err.response?.data;

      // Handle different error types
      if (apiError?.code) {
        switch (apiError.code) {
          case 'INVALID_TOKEN':
            setError('This reset link is invalid or has expired. Please request a new one.');
            break;
          case 'WEAK_PASSWORD':
            if (apiError.details && apiError.requirements) {
              setPasswordRequirements(apiError.details);
              setError('Password does not meet security requirements');
            } else {
              setError(apiError.error);
            }
            break;
          case 'PASSWORD_MISMATCH':
            setError('Passwords do not match');
            break;
          case 'RATE_LIMIT_EXCEEDED':
            setError('Too many attempts. Please try again later.');
            break;
          default:
            setError(apiError.error || 'Failed to reset password. Please try again.');
        }
      } else {
        setError(err.response?.data?.error || 'Unable to connect to server. Please check your internet connection and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while verifying token
  if (isVerifyingToken) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h2>Verifying Reset Link...</h2>
          </div>
          <div className="loading-content">
            <span className="spinner"></span>
            <p>Please wait while we verify your reset link</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error if token verification failed
  if (!tokenVerification) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h2>Invalid Reset Link ❌</h2>
          </div>

          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>

          <div className="info-box">
            <h4>What happened?</h4>
            <ul>
              <li>The reset link may have expired (links are valid for 1 hour)</li>
              <li>The link may have already been used</li>
              <li>The link may be malformed or incomplete</li>
            </ul>
          </div>

          <div className="auth-footer">
            <Link to="/forgot-password" className="auth-button">
              Request New Reset Link
            </Link>
            <p style={{ marginTop: '1rem' }}>
              <Link to="/login" className="auth-link">
                Back to Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show success message after successful reset
  if (isSubmitted) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h2>Password Reset Successfully! ✅</h2>
          </div>

          <div className="success-message">
            <div className="success-icon">🎉</div>
            <p>
              Your password has been reset successfully for <strong>{tokenVerification.user?.email}</strong>
            </p>
          </div>

          <div className="info-box">
            <h4>What's next:</h4>
            <ul>
              <li>You'll be redirected to the login page automatically</li>
              <li>Use your new password to sign in</li>
              <li>For security, you may need to sign in on all your devices again</li>
            </ul>
          </div>

          <div className="auth-footer">
            <Link to="/login" className="auth-button">
              Sign In Now
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Set New Password 🔐</h2>
          <p>Create a strong password for <strong>{tokenVerification.user?.email}</strong></p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {passwordRequirements.length > 0 && (
            <div className="requirements-box">
              <h4>Password Requirements:</h4>
              <ul>
                {passwordRequirements.map((req, index) => (
                  <li key={index}>{req}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your new password"
                required
                minLength={8}
                autoComplete="new-password"
                autoFocus
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? '👁️' : '🙈'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your new password"
              required
              autoComplete="new-password"
            />
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={isLoading || !formData.password || !formData.confirmPassword}
          >
            {isLoading ? (
              <span className="loading-content">
                <span className="spinner"></span>
                Resetting Password...
              </span>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>

        <div className="security-info">
          <h4>🔒 Password Security Tips</h4>
          <ul>
            <li>Use at least 8 characters</li>
            <li>Include uppercase and lowercase letters</li>
            <li>Add numbers and special characters</li>
            <li>Avoid using personal information</li>
            <li>Don't reuse passwords from other accounts</li>
          </ul>
        </div>

        <div className="auth-footer">
          <p>
            Remember your password?{' '}
            <Link to="/login" className="auth-link">
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;